import React from "react";
import Lexical from "lexical";
import {Property} from "csstype";
import {useTranslation} from "react-i18next";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import {Tooltip} from "../../common/Tooltip";
import {EditorType} from "../../../models/CellContent";
import {ImageKey2Src} from "../../../assets/BehaviorImage";
import {BehaviorImageSelector} from "./BehaviorImageSelector";
import {debug_log} from "../../../utils/debug";
import {LexicalEditor} from "./LexicalEditor";
import {JcEditorState} from "../../../models/CellContent/JcEditorState";

type BehaviorLineProps = {
    sortKey: string;
    isEdit: boolean;
    isSelected: boolean;
    readOnly: boolean;
    image: string;
    editorState: JcEditorState;
    editorType: EditorType;
    maxWidth: number;
    lineBackgroundColor: Property.BackgroundColor;

    Remove: (key: string) => void;
    Append: () => void;
    ChangeImage: (key: string, image: string) => void;
    ChangeEditorState: (key: string, editor_state: JcEditorState) => void;
}
export const BehaviorLine = (props: BehaviorLineProps) => {
    const {t} = useTranslation();
    const [isLineEdit, setIsLineEdit] = React.useState<boolean>(false);
    const [showBehaviorImageSelector, setShowBehaviorImageSelector] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!props.isEdit) {
            debug_log("BehaviorLine.isEdit:" + props.isEdit);

            setIsLineEdit(false);
            setShowBehaviorImageSelector(false);
        }
    }, [props.isEdit])

    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
    } = useSortable({id: props.sortKey});
    if (transform) {
        // y 方向に固定
        transform.x = 0;
        transform.scaleX = 1;
        transform.scaleY = 1;
    }

    const transition_or_undefined = transition === null ? undefined : transition;
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition: transition_or_undefined,
        backgroundColor: props.editorType === 'behavior' ? undefined : props.lineBackgroundColor,
    };

    const handleEdit = () => {
        if (props.isSelected) {
            !props.readOnly && setIsLineEdit(true);
        }
    }

    const handleChange = (editor_state: Lexical.EditorState) => {
        props.ChangeEditorState(props.sortKey, props.editorState.withLexicalEditorState(editor_state));
    }

    const handleBlur = () => {
        setIsLineEdit(false);
    }

    const handleSelectImage = (image: string) => {
        setShowBehaviorImageSelector(false);
        props.ChangeImage(props.sortKey, image);
    }

    return (
        <div {...attributes}
             className={(props.editorType === 'behavior' ? 'behavior_line' : 'action_line') + ' ' + (isLineEdit ? 'is_edit' : '')}
             ref={setNodeRef}
             style={style}
             role={undefined} /* attributes で設定されるのを抑制 */
        >
            {props.editorType === 'behavior' && <>
              <img className="behavior-image"
                   alt={props.image}
                   src={ImageKey2Src(props.image)}
                   onClick={() => props.isSelected && !props.readOnly && setShowBehaviorImageSelector(!showBehaviorImageSelector)}>
              </img>
                {showBehaviorImageSelector && (
                    <BehaviorImageSelector
                        selectedImageKey={props.image}
                        SelectImage={(image) => handleSelectImage(image)}
                        onClickCloseButton={() => setShowBehaviorImageSelector(false)}
                    />
                )}
            </>
            }

            <div className={"line_editor " + (isLineEdit ? 'is_edit' : '')}
                 onClick={() => !isLineEdit && handleEdit()}
            >
                <LexicalEditor
                    editorState={props.editorState.getInternalEditorState()}
                    isEdit={isLineEdit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    showToolbar={isLineEdit}
                />
                {!isLineEdit && props.editorState.isEmpty() &&
                  <span className="empty-description">{t("説明を追加")}</span>}
            </div>
            {!props.isEdit || isLineEdit || showBehaviorImageSelector ? null :
                <div className="popup-ui" onClick={e => e.stopPropagation()}>
                    <Tooltip title={t("追加")}>
                        <span className="popup-ui-button add" onClick={() => props.Append()}/>
                    </Tooltip>
                    <Tooltip title={t("上下移動")}>
                        <span className="popup-ui-button vertical_move" {...listeners}/>
                    </Tooltip>
                    <Tooltip title={t("削除")}>
                        <span className="popup-ui-button delete" onClick={() => props.Remove(props.sortKey)}/>
                    </Tooltip>
                </div>
            }
        </div>
    );
}
